import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Checkbox } from "@/components/ui/CheckBox";
import "./style.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { IMAGES } from "@/assets/IMAGES";
import CommonInput from "@/components/custom-component/CommonInput";
import { LoginFormSchema, loginSchema } from "@/schema/LoginSchema";
import { useEffect, useRef, useState } from "react";
import { useToast } from "@/components/ui/UseToast";
import ReCAPTCHA from "react-google-recaptcha";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/app/store";
import { useDispatch } from "react-redux";
import { extractAndStoreDomain, login } from "@/features/auth/AuthSlice";
import { getLinks } from "@/utils/Helper";
import { LoadingButton } from "@/components/ui/loading-button/LoadingButton";

const {
  VITE_APP_SITEKEY,
  VITE_APP_ENABLE_CAPTCHA,
  VITE_IS_PRODUCTION,
  VITE_IS_LOCAL,
  VITE_LOCAL_URL,
} = import.meta.env;

const AuthenticationPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const recaptchaRef = useRef<ReCAPTCHA | null>(null); // Reference to reCAPTCHA
  const dispatch = useDispatch<AppDispatch>();
  const userPersona = localStorage.getItem("userType");

  const [isCaptchaError, setCaptchaError] = useState(false);
  const { loading, error, isEmailVerify } = useSelector(
    (state: RootState) => state.auth
  );

  const links = getLinks() || ["/"]; // Return landing page as default;
  const isAuthenticated = Boolean(localStorage.getItem("userToken"));
  const location = useLocation();

  useEffect(() => {
    if (
      isAuthenticated &&
      userPersona === "Admin" &&
      location.pathname === "/login"
    ) {
      navigate(links[0]);
    }
  }, [location]);
  const email = localStorage.getItem("email") ?? "";

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: email,
      rememberMe: false,
    },
  });

  useEffect(() => {
    return () => {
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    };
  }, []);

  const onSubmit = async (data: LoginFormSchema) => {
    const enableCaptcha = VITE_APP_ENABLE_CAPTCHA !== "false"; // Default to true if not specified

    if (enableCaptcha) {
      const recaptchaValue = recaptchaRef.current?.getValue();
      if (!recaptchaValue) {
        setCaptchaError(true);
        toast({
          variant: "destructive",
          description: "Please complete the CAPTCHA to proceed.",
        });
        return;
      }
    }
    extractAndStoreDomain(window.location.origin);    // Dispatch the login thunk
    dispatch(
      login({
        email: data.email.toLocaleLowerCase(),
        rememberme: data.rememberMe ?? false,
        domain:
          VITE_IS_LOCAL === "true"
            ? VITE_LOCAL_URL
            : VITE_IS_PRODUCTION === "true"
              ? window.location.hostname
              : window.location.host,
      })
    )
      .unwrap()
      .then((response) => {
        toast({
          variant: "success",
          description: (
            <div className="flex items-center space-x-2 w-full h-10">
              <img src={IMAGES.Status_icon} alt="Success" className="w-6 h-6" />
              <span>{response.message}</span>
            </div>
          ),
        });
        navigate("/login-otp", {
          state: { email: data.email, emailResponse: response },
        });
      })
      .catch((error) => {
        toast({
          variant: "destructive",
          description: error,
        });
      });
  };

  const onRecaptchaChange = (value: any) => {
    setCaptchaError(false);
  };

    const handleRememberMe = (e: boolean) => {
      setValue("rememberMe", e);
      if (e == false) {
        localStorage.removeItem("email");
        localStorage.removeItem("password");
      }
    };


  return (
    <div className="loginpage-image ">
      <div className="max-w-[1800px] mx-auto xs:mx-0">
        <div className="flex sm:mx-16 justify-center sm:justify-between items-center h-dvh xs:mx-0">
          <div className="flex flex-col gap-2 xs:gap-0"></div>
          <div>
            <Card className="px-16 max-w-md bg-white/5 backdrop-blur-lg rounded-3xl border border-white/40 shadow-lg p-2">
              <CardHeader>
                <CardTitle>
                  <div className="gap-2 flex justify-center items-center">
                    <div>
                      <img
                        src={IMAGES.logo_icon}
                        height={110}
                        width={100}
                        alt="Main logo"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <p className="text-primary italic font-semibold text-[1.8rem] text-nowrap">
                        Login to Admin Portal
                      </p>
                    </div>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
                  <div>
                    <Controller
                      name="email"
                      rules={{
                        required: "Required",
                        maxLength: {
                          value: 50,
                          message: "Should be 50 characters or less",
                        },
                        pattern: {
                          value:
                            /^(?!.*\.\.)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i,
                          message: "Incorrect email format",
                        },
                      }}
                      control={control}
                      render={({ field }) => (
                        <CommonInput
                          {...field}
                          name="email"
                          type="email"
                          placeholder="Enter your email"
                          error={errors.email?.message}
                          isIconAllow={true}
                          className="w-full"
                        />
                      )}
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Controller
                      name="rememberMe"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          id="rememberme"
                          checked={field.value}
                          onCheckedChange={handleRememberMe}
                          className="text-white border-checkboxborder"
                        />
                      )}
                    />

                    <label
                      htmlFor="rememberme"
                      className="font-poppins text-textgray text-sm font-medium leading-none"
                    >
                      Remember Me
                    </label>
                  </div>

                  {/* Conditionally render reCAPTCHA */}
                  {VITE_APP_ENABLE_CAPTCHA && (
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      theme="dark"
                      sitekey={VITE_APP_SITEKEY}
                      onChange={onRecaptchaChange}
                      size="normal"
                    />
                  )}

                  {isCaptchaError && (
                    <>
                      <span className="text-red-400 text-xs font-poppins">
                        Please complete the reCaptcha
                      </span>
                    </>
                  )}

                  <LoadingButton
                    loading={loading}
                    type="submit"
                    className="!w-full hover:bg-secondary text-black"
                  >
                    Login
                  </LoadingButton>
                </form>
              </CardContent>
            </Card>

            {/* <div className="flex justify-center items-center mt-5 flex-col">
              <div>
                <p className="Poppins text-textgray text-sm">
                  By using this app you agree to our
                </p>
              </div>
              <div>
                <p className="Poppins text-textgray text-sm">
                  <Link
                    className="text-primary underline"
                    to="/privacy-statement"
                  >
                    Privacy Statement
                  </Link>{" "}
                  -{" "}
                  <Link
                    className="text-primary underline"
                    to="/terms-and-conditions"
                  >
                    Terms & condition{" "}
                  </Link>
                </p>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthenticationPage;
